import { v4 as uuidv4 } from 'uuid';
import { Client, IndividualClient } from '../types/client';
import { Order } from '../types/order';

/**
 * Normalizes a string for comparison by trimming and converting to lowercase
 */
export const normalizeString = (str: string | undefined | null): string => {
  if (!str) return '';
  return str.trim().toLowerCase();
};

/**
 * Builds a full address string from order components for comparison
 */
export const buildFullAddress = (order: Order): string => {
  const parts = [order.streetAddress, order.addressLine2, order.city, order.province, order.postalCode]
    .filter(part => part && part.trim())
    .map(part => normalizeString(part));

  return parts.join(' ');
};

/**
 * Match types for duplicate detection
 */
export type MatchType = 'exact' | 'fuzzy' | 'none';

export interface ClientMatch {
  client: Client;
  matchType: MatchType;
  matchedFields: string[]; // Which fields matched (name, email, address)
}

/**
 * Checks if two normalized strings are an exact match
 */
const isExactMatch = (str1: string, str2: string): boolean => {
  return str1 === str2 && str1.length > 0;
};

/**
 * Checks if two normalized strings are a fuzzy match
 * Fuzzy match means they're very similar but not identical
 * (e.g., extra/missing whitespace, punctuation differences)
 */
const isFuzzyMatch = (str1: string, str2: string): boolean => {
  if (str1 === str2) return false; // Exact matches are handled separately
  if (!str1 || !str2) return false;

  // Remove all non-alphanumeric characters for fuzzy comparison
  const clean1 = str1.replace(/[^a-z0-9]/g, '');
  const clean2 = str2.replace(/[^a-z0-9]/g, '');

  return clean1 === clean2 && clean1.length > 0;
};

/**
 * Finds matching clients based on name, email, and address
 * Returns both exact and fuzzy matches
 */
export const findMatchingClients = (
  order: Order,
  clients: Client[]
): { exactMatches: ClientMatch[]; fuzzyMatches: ClientMatch[] } => {
  const exactMatches: ClientMatch[] = [];
  const fuzzyMatches: ClientMatch[] = [];

  // Normalize order data
  const orderName = normalizeString(order.customerName);
  const orderEmail = normalizeString(order.email);
  const orderAddress = buildFullAddress(order);

  // If all fields are empty, no matches possible
  if (!orderName && !orderEmail && !orderAddress) {
    return { exactMatches, fuzzyMatches };
  }

  for (const client of clients) {
    // For retailers, use retailerName; for individuals, use name
    const clientName = normalizeString(client.type === 'individual' ? client.name : client.retailerName);
    const clientEmail = normalizeString(client.email);
    const clientAddress = normalizeString(client.address);

    let nameMatch: MatchType = 'none';
    let emailMatch: MatchType = 'none';
    let addressMatch: MatchType = 'none';

    // Check name matching
    if (orderName && clientName) {
      if (isExactMatch(orderName, clientName)) {
        nameMatch = 'exact';
      } else if (isFuzzyMatch(orderName, clientName)) {
        nameMatch = 'fuzzy';
      }
    }

    // Check email matching
    if (orderEmail && clientEmail) {
      if (isExactMatch(orderEmail, clientEmail)) {
        emailMatch = 'exact';
      } else if (isFuzzyMatch(orderEmail, clientEmail)) {
        emailMatch = 'fuzzy';
      }
    }

    // Check address matching
    if (orderAddress && clientAddress) {
      if (isExactMatch(orderAddress, clientAddress)) {
        addressMatch = 'exact';
      } else if (isFuzzyMatch(orderAddress, clientAddress)) {
        addressMatch = 'fuzzy';
      }
    }

    // Determine overall match type
    const matchedFields: string[] = [];
    let hasExactMatch = false;
    let hasFuzzyMatch = false;

    if (nameMatch === 'exact') {
      matchedFields.push('name');
      hasExactMatch = true;
    } else if (nameMatch === 'fuzzy') {
      matchedFields.push('name');
      hasFuzzyMatch = true;
    }

    if (emailMatch === 'exact') {
      matchedFields.push('email');
      hasExactMatch = true;
    } else if (emailMatch === 'fuzzy') {
      matchedFields.push('email');
      hasFuzzyMatch = true;
    }

    if (addressMatch === 'exact') {
      matchedFields.push('address');
      hasExactMatch = true;
    } else if (addressMatch === 'fuzzy') {
      matchedFields.push('address');
      hasFuzzyMatch = true;
    }

    // Requirement: Match on name AND email AND address
    // For exact match: All three fields must match exactly
    // For fuzzy match: All three fields must match (exact or fuzzy), with at least one fuzzy

    const namePresent = orderName && clientName;
    const emailPresent = orderEmail && clientEmail;
    const addressPresent = orderAddress && clientAddress;

    // Count how many fields are present and how many match
    const fieldsPresent = [namePresent, emailPresent, addressPresent].filter(Boolean).length;
    const fieldsMatched = matchedFields.length;

    // Only consider it a match if all present fields match
    if (fieldsPresent > 0 && fieldsMatched === fieldsPresent) {
      if (hasExactMatch && !hasFuzzyMatch) {
        // All matches are exact
        exactMatches.push({
          client,
          matchType: 'exact',
          matchedFields
        });
      } else if (hasFuzzyMatch) {
        // At least one fuzzy match
        fuzzyMatches.push({
          client,
          matchType: 'fuzzy',
          matchedFields
        });
      }
    }
  }

  return { exactMatches, fuzzyMatches };
};

/**
 * Finds an existing client by name, or creates a new one if none exists.
 * Returns the clientId to be used for order creation.
 *
 * @param customerName - The customer name from the order
 * @param existingClients - Array of all existing clients
 * @param orderData - Optional order data to populate the new client
 * @returns Object with clientId and whether client was created
 */
export const findOrCreateClientByName = (
  customerName: string,
  existingClients: Client[],
  orderData?: Partial<Order>
): { clientId: string; client: Client; isNewClient: boolean } => {
  const normalizedName = normalizeString(customerName);

  // Look for existing individual client with matching name
  const existingClient = existingClients.find(
    client => client.type === 'individual' && normalizeString(client.name) === normalizedName
  );

  if (existingClient) {
    return {
      clientId: existingClient.id,
      client: existingClient,
      isNewClient: false
    };
  }

  // Create new individual client
  const newClient: IndividualClient = {
    id: uuidv4(),
    type: 'individual',
    name: customerName,
    email: orderData?.email || '',
    phone: orderData?.phone || '',
    address: orderData ? buildFullAddressFromOrder(orderData) : '',
    notes: `Auto-created from order on ${new Date().toLocaleDateString()}`,
    preferences: '',
    createdAt: new Date().toISOString()
  };

  return {
    clientId: newClient.id,
    client: newClient,
    isNewClient: true
  };
};

/**
 * Builds a full address string from order data for client creation
 */
const buildFullAddressFromOrder = (order: Partial<Order>): string => {
  const parts = [order.streetAddress, order.addressLine2, order.city, order.province, order.postalCode].filter(
    part => part && part.trim()
  );

  return parts.join(', ');
};
